/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — import-wizard.ts
   Drag-and-drop zones, column-mapping wizard UI, file selection.
   ═══════════════════════════════════════════════════════════════════════ */

import { state } from './state';
import { esc, el } from './utils';
import {
  parseCSV,
  normalizeHeader,
  readFileText,
  WIZARD_FIELDS,
} from './import-parser';

// ─── Module-level wizard state ────────────────────────────────────────────
// Kept here (not in the orchestrator) so wizard rendering and mapping
// extraction can share it cleanly. The orchestrator reads via getters.

let wizardRawRows: string[][] = [];
let wizardHeaders: string[] = [];

export function getWizardRawRows(): string[][] {
  return wizardRawRows;
}

export function getWizardHeaders(): string[] {
  return wizardHeaders;
}

export function clearWizardState(): void {
  wizardRawRows = [];
  wizardHeaders = [];
}

// ─── Drop-zone handlers ───────────────────────────────────────────────────

export function zoneDrag(e: DragEvent, zoneId: string): void {
  e.preventDefault();
  el(zoneId)?.classList.add('drag-over');
}

export function zoneDragEnd(zoneId: string): void {
  el(zoneId)?.classList.remove('drag-over');
}

export function zoneDrop(e: DragEvent, zoneId: string, key: string): void {
  e.preventDefault();
  zoneDragEnd(zoneId);
  const file = e.dataTransfer?.files[0];
  if (file) setImportFile(zoneId, key, file);
}

export function fileChosen(input: HTMLInputElement, zoneId: string, key: string): void {
  if (input.files?.[0]) setImportFile(zoneId, key, input.files[0]);
}

function setImportFile(zoneId: string, key: string, file: File): void {
  (state.importFiles as Record<string, File | null>)[key] = file;
  const zone = el(zoneId);
  if (zone) zone.classList.add('has-file');
  const nameEl = el(zoneId + '-name');
  const hintEl = el(zoneId + '-hint');
  if (nameEl) nameEl.textContent = file.name;
  if (hintEl) hintEl.textContent = '';

  // Trigger wizard — read the file and show column mapping
  showWizard(file);
}

async function showWizard(file: File): Promise<void> {
  try {
    const text = await readFileText(file);
    wizardRawRows = parseCSV(text);
    if (wizardRawRows.length < 2) { showStatus('error', 'File appears to be empty.'); return; }
    wizardHeaders = wizardRawRows[0];

    const stepUpload = el('import-step-upload');
    const stepMap = el('import-step-map');
    if (stepUpload) stepUpload.style.display = 'none';
    if (stepMap) stepMap.style.display = 'block';

    renderWizardMappings();
    renderWizardPreview();

    // Clear any previous import status
    const importStatus = el('import-status');
    if (importStatus) importStatus.innerHTML = '';
  } catch (err: any) {
    showStatus('error', 'Could not read file: ' + err.message);
  }
}

export function importWizardBack(): void {
  const stepUpload = el('import-step-upload');
  const stepMap = el('import-step-map');
  if (stepUpload) stepUpload.style.display = 'block';
  if (stepMap) stepMap.style.display = 'none';
  wizardRawRows = [];
  wizardHeaders = [];

  // Reset the file input so they can pick a new file
  const zone = el('zone-combined');
  if (zone) zone.classList.remove('has-file');
  const nameEl = el('zone-combined-name');
  const hintEl = el('zone-combined-hint');
  if (nameEl) nameEl.textContent = '';
  if (hintEl) hintEl.textContent = 'Drop file here or click to browse';
  (state.importFiles as Record<string, File | null>)['combined'] = null;
  const fileInput = zone?.querySelector('input[type="file"]') as HTMLInputElement | null;
  if (fileInput) fileInput.value = '';
}

function renderWizardMappings(): void {
  const container = el('wizard-mappings');
  if (!container) return;
  const normalizedHeaders = wizardHeaders.map(normalizeHeader);

  let html = '';
  for (const field of WIZARD_FIELDS) {
    // Auto-detect best match
    let bestIdx = -1;
    for (const candidate of field.autoCandidates) {
      const idx = normalizedHeaders.indexOf(candidate);
      if (idx !== -1) { bestIdx = idx; break; }
    }

    const optionalTag = field.required ? '' : ' <span class="wiz-optional">(optional)</span>';
    html += '<div class="wiz-field">';
    html += '<label>' + esc(field.label) + optionalTag + '</label>';
    html += '<select id="wiz-' + field.key + '" data-wiz-change="true"' + (bestIdx >= 0 ? ' class="mapped"' : '') + '>';
    html += '<option value="">-- Skip --</option>';
    for (let i = 0; i < wizardHeaders.length; i++) {
      const sel = i === bestIdx ? ' selected' : '';
      html += '<option value="' + i + '"' + sel + '>' + esc(wizardHeaders[i]) + '</option>';
    }
    html += '</select>';
    html += '</div>';
  }
  container.innerHTML = html;
}

function renderWizardPreview(): void {
  const container = el('wizard-preview');
  if (!container) return;

  // Show up to 8 data rows
  const previewCount = Math.min(wizardRawRows.length - 1, 8);
  let html = '<table><thead><tr>';
  for (const h of wizardHeaders) {
    html += '<th>' + esc(h) + '</th>';
  }
  html += '</tr></thead><tbody>';
  for (let i = 1; i <= previewCount; i++) {
    html += '<tr>';
    for (let j = 0; j < wizardHeaders.length; j++) {
      const val = wizardRawRows[i]?.[j] || '';
      html += '<td>' + esc(val) + '</td>';
    }
    html += '</tr>';
  }
  if (wizardRawRows.length - 1 > previewCount) {
    html += '<tr><td colspan="' + wizardHeaders.length + '" style="color:var(--muted);font-style:italic">... and ' + (wizardRawRows.length - 1 - previewCount) + ' more rows</td></tr>';
  }
  html += '</tbody></table>';
  container.innerHTML = html;
}

export function getWizardMapping(): Record<string, number> {
  const mapping: Record<string, number> = {};
  for (const field of WIZARD_FIELDS) {
    const sel = document.getElementById('wiz-' + field.key) as HTMLSelectElement | null;
    if (sel && sel.value !== '') {
      mapping[field.key] = parseInt(sel.value, 10);
    }
  }
  return mapping;
}

function showStatus(type: string, msg: string, trusted?: boolean): void {
  const importStatus = el('import-status');
  if (importStatus) importStatus.innerHTML =
    '<div class="import-status ' + type + '">' + (trusted ? msg : esc(msg)) + '</div>';
}
